import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, Redirect, Route, Switch } from 'react-router-dom';

import type { IIntegrationUITab } from '@feathr/components';
import {
  IntegrationActionMenu,
  IntegrationPageShell,
  IntegrationSetupSteps,
  noop,
} from '@feathr/components';
import { IntegrationPageFrame } from '@feathr/extender/App/Page';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import RaisersEdgeActivitiesTab from './RaisersEdgeActivitiesTab';
import { getRaisersEdgeConfig } from './raisersEdgeConfig';
import { useRaisersEdgePollCount, useRaisersEdgeSave } from './RaisersEdgeIntegrationPageV2.hooks';
import { getBusyReason, getUnsavedChangesPrompt } from './RaisersEdgeIntegrationPageV2.utils';
import RaisersEdgeMappingsTab from './RaisersEdgeMappingsTab';
import {
  useRaisersEdgeMappingDraft,
  useRaisersEdgeMappingsRefresh,
} from './RaisersEdgeMappingsTab/RaisersEdgeMappingsTab.hooks';
import RaisersEdgeSettingsTab from './RaisersEdgeSettingsTab';
import { useRaisersEdgeSyncSummary } from './RaisersEdgeSyncSummary/RaisersEdgeSyncSummary.hooks';
import { getHeaderLastSyncedAt } from './RaisersEdgeSyncSummary/RaisersEdgeSyncSummary.utils';
import { useRaisersEdgeConnectionControls } from './useRaisersEdgeConnectionControls';
import { useRaisersEdgeSetup } from './useRaisersEdgeSetup';

/** v1 tab path → v2 tab path, so v1 URLs (bookmarks, help links) keep working. */
const LEGACY_TAB_REDIRECTS: Readonly<Record<string, string>> = {
  overview: 'mapping',
  unsubscribes: 'settings',
};

function RaisersEdgeIntegrationPageV2(): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const { BlackbaudRaisersEdgeIntegrations } = useStore();

  const integrations = BlackbaudRaisersEdgeIntegrations.list();
  // An account has at most one integration. Check length first: MobX warns on an out-of-bounds read.
  const integration = integrations.models.length ? integrations.models[0] : undefined;
  const config = useMemo(() => getRaisersEdgeConfig(t), [t]);
  const basePath = localUrl('/settings/integrations/raisers-edge');
  const mappingDraft = useRaisersEdgeMappingDraft(integration);
  const { handleSave, isDirty, isSaving, saveDisabledReason } = useRaisersEdgeSave(
    integration,
    mappingDraft,
  );
  const { handleDisconnect, handlePause, handleResume, isChangingState } =
    useRaisersEdgeConnectionControls(integration);
  const unsavedChangesPrompt = useMemo(() => getUnsavedChangesPrompt(basePath, t), [basePath, t]);
  const busyReason = getBusyReason({ isChangingState, isSaving }, t);
  const menuDisabledReasons = busyReason
    ? { disconnect: busyReason, pause: busyReason, resume: busyReason }
    : undefined;
  // Only a connected or paused integration has tabs.
  const isActive = !!integration?.isActive;
  const pollCount = useRaisersEdgePollCount(isActive && !isSaving);
  const syncSummary = useRaisersEdgeSyncSummary(integration, isSaving, pollCount);
  useRaisersEdgeMappingsRefresh(integration, pollCount);
  const { completedCount, handleAction, loadingKey } = useRaisersEdgeSetup();

  function renderTab(tab: IIntegrationUITab): ReactNode {
    if (!integration) {
      return null;
    }
    const sections = config.toggleSections.filter((section) => section.tab === tab.key);

    switch (tab.key) {
      case 'mappings':
        return (
          <RaisersEdgeMappingsTab
            config={config}
            disabledReason={busyReason}
            draft={mappingDraft}
            integration={integration}
            key={integration.id}
            sections={sections}
            syncSummary={syncSummary}
          />
        );

      case 'activities':
        return (
          <RaisersEdgeActivitiesTab
            disabledReason={busyReason}
            integration={integration}
            // A replaced model must not keep the old one's state.
            key={integration.id}
            sections={sections}
          />
        );

      case 'settings':
        return (
          <RaisersEdgeSettingsTab
            disabledReason={busyReason}
            integration={integration}
            // A replaced model must not keep the old one's loaded solicit codes.
            key={integration.id}
            onDisconnect={handleDisconnect}
            sections={config.settingsSections}
          />
        );

      default:
        return null;
    }
  }

  return (
    <Switch>
      {Object.entries(LEGACY_TAB_REDIRECTS).map(([from, to]) => (
        <Redirect exact={true} from={`${basePath}/${from}`} key={from} to={`${basePath}/${to}`} />
      ))}
      <Route>
        <Prompt message={unsavedChangesPrompt} when={isDirty} />
        <IntegrationPageShell
          actionMenu={
            integration && (
              <IntegrationActionMenu
                crmName={config.crmName}
                disabledReasons={menuDisabledReasons}
                name={'raisers-edge-actions'}
                onDisconnect={handleDisconnect}
                onPause={handlePause}
                onResume={handleResume}
                state={integration.integrationState}
              />
            )
          }
          basePath={basePath}
          config={config}
          connectionState={integration?.integrationState ?? 'not_connected'}
          importDisabledReason={t('Import is not available yet.')}
          isDirty={isDirty}
          isSaving={isSaving}
          lastSyncedAt={
            isActive ? getHeaderLastSyncedAt(syncSummary, integration?.lastSynced) : undefined
          }
          loading={integrations.isPending}
          // v1 has no import endpoint, so Import only renders, disabled with a reason.
          onImport={noop}
          onSave={handleSave}
          PageComponent={IntegrationPageFrame}
          renderTab={renderTab}
          // A save already shows its own loading state; a state change blocks it with a reason.
          saveDisabledReason={isChangingState ? busyReason : saveDisabledReason}
          // Before a connection, the setup steps replace the tabs and actions.
          setup={
            !isActive && (
              <IntegrationSetupSteps
                completedCount={completedCount}
                loadingKey={loadingKey}
                name={'raisers-edge-setup'}
                onAction={handleAction}
                steps={config.setupSteps}
              />
            )
          }
        />
      </Route>
    </Switch>
  );
}

export default observer(RaisersEdgeIntegrationPageV2);
